import { createMemo, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Badge } from "@/components/ui/badge";
import { SubcategoryChipList } from "@/domains/freelance/taxonomy/components/subcategory-chip-list";
import { categoryLabel } from "@/domains/freelance/taxonomy/store/taxonomy-store";
import type { JobSubcategoryKey } from "@/domains/freelance/taxonomy/types";
import type { JobCategory } from "@/domains/job-offer/create/constants";
import { shownOfferSubcategories } from "@/domains/job-offer/utils/offer-subcategories-display";
import { cn } from "@/libs/cn";

interface OfferSubcategoryChipsProps {
  category: JobCategory | null | undefined;
  keys: readonly JobSubcategoryKey[] | null | undefined;
  inactive?: boolean;
  class?: string;
}

/**
 * Chips with the subcategories ("ambiti") an offer declares. Renders nothing
 * when the offer covers the whole category or nothing is left after filtering.
 */
export function OfferSubcategoryChips(props: OfferSubcategoryChipsProps) {
  const { t } = useI18n();
  const shown = createMemo(() =>
    shownOfferSubcategories(props.category, props.keys),
  );

  return (
    <SubcategoryChipList
      keys={shown()}
      label={t("jobOffers.refine.subcategories.label")}
      inactive={props.inactive}
      class={props.class}
    />
  );
}

/**
 * The offer's category badge followed by its ambiti, for the surfaces that had
 * no category before (headers and cards). Nothing without a category.
 */
export function OfferCategoryRow(props: {
  category: JobCategory | null | undefined;
  keys: readonly JobSubcategoryKey[] | null | undefined;
  inactive?: boolean;
  class?: string;
}) {
  return (
    <Show when={props.category}>
      {(category) => (
        <div class={cn("flex flex-wrap items-center gap-2", props.class)}>
          <Badge
            variant={props.inactive ? "secondary" : "light-primary"}
            size="sm"
            class="rounded-full"
          >
            {categoryLabel(category())}
          </Badge>
          <OfferSubcategoryChips
            category={category()}
            keys={props.keys}
            inactive={props.inactive}
          />
        </div>
      )}
    </Show>
  );
}
